import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah9bebp14a1h3mr6g4g";
const MAX_DETAIL = 500;

export default function TipsForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [detailLen, setDetailLen] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("loading");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
      const lower = String(serverMsg).toLowerCase();
      const isSpam = lower.includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
        setDetailLen(0);
      } else {
        setStatus("error");
        setFormError(String(serverMsg));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <form
      id="gt-tips-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="gt-nickname" className="text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="gt-nickname"
            name="nickname"
            type="text"
            maxLength={40}
            required
            placeholder="怎么称呼你"
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="gt-email" className="text-sm font-medium text-foreground-800">
            邮箱
          </label>
          <input
            id="gt-email"
            name="email"
            type="email"
            required
            placeholder="用于接收核对进度"
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="gt-type" className="text-sm font-medium text-foreground-800">
          投递类型
        </label>
        <select
          id="gt-type"
          name="type"
          required
          defaultValue=""
          className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
        >
          <option value="" disabled>
            请选择投递类型
          </option>
          <option value="爆料线索">爆料线索</option>
          <option value="内容更正">内容更正 / 补充说明</option>
          <option value="订阅更新">订阅档案更新</option>
          <option value="合作沟通">合作与其他</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label htmlFor="gt-detail" className="text-sm font-medium text-foreground-800">
            线索详情
          </label>
          <span className="text-xs text-foreground-500">
            {detailLen}/{MAX_DETAIL}
          </span>
        </div>
        <textarea
          id="gt-detail"
          name="detail"
          rows={4}
          maxLength={MAX_DETAIL}
          required
          onChange={(e) => setDetailLen(e.target.value.length)}
          placeholder="请尽量写明时间、出处与可核对的材料，例如截图发布时间、对应档案编号等。"
          className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
        />
      </div>

      <input
        className="form-honey-trap"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
          {status === "loading" ? "提交中…" : "提交线索"}
        </button>
        <p className="text-xs text-foreground-500 leading-relaxed">
          默认匿名处理，邮箱仅用于同步核对进度，不会公开。
        </p>
      </div>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
          已收到，感谢投递。核对完成后我们会通过邮箱同步进展。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" />
          {formError || "提交失败，请稍后再试。"}
        </p>
      )}
    </form>
  );
}